import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function GoogleButton({ label = "Continue with Google" }) {
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };
  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleGoogle}
      className="w-full rounded-full h-11 border-border bg-[#0b0b0e] hover:bg-[#141418] text-foreground font-medium gap-3"
      data-testid="google-signin-btn"
    >
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.8 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.4 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.4 4 9.8 8.3 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.5l-6.5-5.3C29.6 34.9 26.9 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.6 5.1C9.6 39.6 16.3 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.5 5.3c-.5.5 7.1-5.1 7.1-15 0-1.2-.1-2.3-.4-3.5z"/>
      </svg>
      {label}
    </Button>
  );
}

function OrDivider() {
  return (
    <div className="flex items-center gap-3 my-5" aria-hidden="true">
      <div className="h-px bg-border flex-1" />
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">or</div>
      <div className="h-px bg-border flex-1" />
    </div>
  );
}

function AuthLayout({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="relative hidden md:flex flex-col justify-between p-10 bg-[#0b0b0e] overflow-hidden">
        <div className="grain absolute inset-0" />
        <div className="spark-glow absolute inset-0" />
        <Link to="/" className="relative flex items-center gap-2.5" data-testid="brand-back-home">
          <img src="/logo.png" alt="Client Scout" className="h-9 w-9 rounded-lg object-contain" />
          <span className="font-medium tracking-tight text-lg">Client Scout</span>
        </Link>
        <div className="relative">
          <div className="text-4xl font-medium tracking-tighter leading-tight max-w-sm">
            Hours of lead research, delivered in a single request.
          </div>
          <div className="mt-6 text-sm text-muted-foreground max-w-sm font-light leading-relaxed">
            Join business owners using Client Scout to turn target markets into ranked, actionable outreach lists.
          </div>
        </div>
        <div className="relative text-xs text-muted-foreground">AI-generated; verify before contacting.</div>
      </div>
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <h1 className="text-3xl font-medium tracking-tight">{title}</h1>
          <p className="text-muted-foreground mt-2 text-sm">{subtitle}</p>
          <div className="mt-10">{children}</div>
          <div className="mt-6 text-sm text-muted-foreground">{footer}</div>
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Welcome back");
      nav("/dashboard");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Log in to your Client Scout account."
      footer={<>Don&apos;t have an account? <Link to="/register" className="text-primary hover:underline" data-testid="link-register">Create one</Link></>}
    >
      <GoogleButton />
      <OrDivider />
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full bg-primary hover:bg-[#FF7043] text-white h-11" data-testid="login-submit">
          {loading ? "Logging in…" : "Log in"}
        </Button>
      </form>
    </AuthLayout>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    const r = await register(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Account created — you have 1 free credit");
      nav("/dashboard");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Your first 10-lead list is on us."
      footer={<>Already have an account? <Link to="/login" className="text-primary hover:underline" data-testid="link-login">Log in</Link></>}
    >
      <GoogleButton label="Sign up with Google" />
      <OrDivider />
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <div className="space-y-2">
          <Label htmlFor="email">Work email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password (min 6 chars)</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" />
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full bg-primary hover:bg-[#FF7043] text-white h-11" data-testid="register-submit">
          {loading ? "Creating…" : "Create account"}
        </Button>
      </form>
    </AuthLayout>
  );
}
